<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import { Plus, Trash2 } from "@lucide/svelte"

  import { Badge } from "@/components/ui/badge"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import ConfirmationDialog from "@/components/ui/confirmation-dialog.svelte"
  import AppLayout from "@/layouts/app-layout.svelte"
  import { editUserPath, newUserPath, usersPath } from "@/routes"
  import type { BreadcrumbItem, User } from "@/types"

  interface Props {
    users: User[]
  }

  let { users }: Props = $props()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Users",
      href: usersPath(),
    },
  ]

  let deleteDialogOpen = $state(false)
  let pendingDeleteUserId: number | null = $state(null)

  function deleteUser(userId: number) {
    pendingDeleteUserId = userId
    deleteDialogOpen = true
  }

  function confirmDelete() {
    if (pendingDeleteUserId !== null) {
      router.delete(usersPath({ id: pendingDeleteUserId }))
    }
  }
</script>

<svelte:head>
  <title>Users - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold">Users</h1>
        <p class="text-muted-foreground mt-2">Manage group users</p>
      </div>
      <Button href={newUserPath()}>
        <Plus class="h-4 w-4" />
        Add User
      </Button>
    </div>

    {#if users.length === 0}
      <Card>
        <CardContent class="flex flex-col items-center justify-center p-12">
          <p class="text-muted-foreground mb-4">No users yet</p>
          <Button href={newUserPath()}>Add your first user</Button>
        </CardContent>
      </Card>
    {:else}
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {#each users as user (user.id)}
          <Card>
            <CardHeader>
              <div class="flex items-start justify-between">
                <div>
                  <CardTitle>{user.name}</CardTitle>
                  <CardDescription
                    >User since {new Date(
                      user.created_at,
                    ).toLocaleDateString()}</CardDescription
                  >
                </div>
                <Badge
                  variant={user.role === "admin" ? "default" : "secondary"}
                >
                  {user.role}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div class="flex gap-2">
                <Button
                  variant="outline"
                  href={editUserPath(user.id)}
                  class="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onclick={() => deleteUser(user.id)}
                  class="flex-1"
                >
                  <Trash2 class="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        {/each}
      </div>
    {/if}
  </div>

  <ConfirmationDialog
    bind:open={deleteDialogOpen}
    title="Delete User"
    description="Are you sure you want to delete this user?"
    variant="destructive"
    confirmLabel="Delete"
    onConfirm={confirmDelete}
  />
</AppLayout>
